import { sum } from "./arrays"
import { compare } from "./utils"

type IRankableProduct = {
  stock_code: string
  legacy_stock_code: string | null
  body: string | null
  available_stock: number
}

/**
 * Orders the results of the `/search/product-select` endpoint for display, shared by the product
 * select lists and the top bar stock lookup. The endpoint returns an unordered set of matches, so
 * the order is decided here: where the words appear first (stock code, then legacy code, then body),
 * in stock products before ones with zero stock, then exact stock code matches and in stock products
 * ahead of the rest. Needs `available_stock`, so the endpoint's stock include must be on.
 */
export function rankProductSearchResults<T extends IRankableProduct>(
  products: T[],
  query: string,
  limit = 30,
) {
  const queryParts = query.trim().split(/\s+/)

  const zeroStock: T[] = []
  const rest: T[] = []
  searchSort(products, query)
    .slice(0, limit)
    .forEach(item => {
      if (item.available_stock === 0) {
        zeroStock.push(item)
      } else {
        rest.push(item)
      }
    })

  return sortByScore([...rest, ...zeroStock], queryParts)
}

function itemScore<T extends IRankableProduct>(i: T, queryParts: string[]) {
  return sum([
    queryParts.join(" ").toLowerCase() === i.stock_code.toLowerCase() ? 4 : 0,
    queryParts.filter(p => p.toLowerCase() === i.stock_code.toLowerCase()).length > 0 ? 2 : 0,
    i.available_stock > 0 ? 2 : 0,
  ])
}

function sortByScore<T extends IRankableProduct>(items: T[], queryParts: string[]) {
  // stock code exact match = 4
  // stock code partial match = 1
  // body match = 1
  // in stock = 2
  return items.sort((a, b) => {
    const itemAScore = itemScore(a, queryParts)
    const itemBScore = itemScore(b, queryParts)
    if (itemAScore === itemBScore) {
      return a.available_stock > a.available_stock ? -1 : 1
    }
    return itemBScore - itemAScore
  })
}

function searchSort<T extends IRankableProduct>(results: T[], query: string) {
  const queryParts = query.toUpperCase().trim().split(/\s+/)

  // the endpoint returns nulls for optional columns, so don't only default undefined
  function matchIndex(text: string | null | undefined, query: string) {
    const index = (text ?? "").toUpperCase().indexOf(query)
    return index === -1 ? Infinity : index
  }

  return [...results].sort((a, b) => {
    let result = 0

    for (const part of queryParts) {
      result = compare(matchIndex(a.stock_code, part), matchIndex(b.stock_code, part))
      if (result) {
        return result
      }
      result = compare(matchIndex(a.legacy_stock_code, part), matchIndex(b.legacy_stock_code, part))
      if (result) {
        return result
      }
      result = compare(matchIndex(a.body, part), matchIndex(b.body, part))
      if (result) {
        return result
      }
    }
    return result
  })
}
